import React from 'react';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Link from 'next/link';
import { Grid, Button, Hidden } from '@material-ui/core';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import {
  getProfileOnboardingStep,
  setProfileOnboardingStep,
  setRedirectTo,
} from 'utils/localStorage';
import Alert from '@material-ui/lab/Alert';
import clsx from 'clsx';

import { openModal, setFooter } from 'features/utilsSlice';
import useCurrentUser from 'hooks/useCurrentUser';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useProfileOwner from 'hooks/useProfileOwner';
import Info from './Info';
import InfluencerNetworks from './InfluencerNetworks';
import ProfilePreviewDialog from './ProfilePreviewDialog';
import WorkingStats from './WorkingStats';
import InventoryTabs from './InventoryTabs';
import ProfileCover from 'components/profile/ProfileCover';
import ProfileInfo from 'components/profile/ProfileInfo';
import Bio from 'components/Bio';
import Header from 'components/header/Header';
import PurpleCard from 'components/PurpleCard';
import ExternalLink from 'components/ExternalLink';
import LoggedInHeader from 'components/header/LoggedInHeader';
import InfluencerActionsSimplified from './InfluencerActionsSimplified';
import styles from 'styles/components/profile/Profile.module.scss';

const removeDuplicates = array =>
  array.filter((el, index) => index === array.findIndex(item => item.id === el.id));

const Profile = ({
  profile,
  setProfile,
  isDialog,
  handleClose,
  selectedNetworkIds,
  handleInvite,
  gig,
}) => {
  const cartItems = useSelector(state => state.cart.cart_items);
  const isLoggedIn = useIsLoggedIn();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const profileOwner = useProfileOwner(profile);

  const inventories = profileOwner
    ? profile.inventories
    : profile.inventories.filter(
        inventory => inventory.active && !inventory.inventory_items.every(item => !item.active)
      );

  const influencerNetworks = inventories.map(inventory => inventory.influencer_network);

  const uniqInfluencerNetworks = removeDuplicates(influencerNetworks);

  const networks = inventories
    .map(i => ({
      space: i.inventory_space.name,
      network: i.influencer_network.network,
    }))
    .reduce((prev, obj) => {
      const availableSpaces = prev[obj.network.id]?.availableSpaces || [];

      prev[obj.network.id] = {
        ...obj.network,
        availableSpaces: [...availableSpaces, obj.space],
      };

      return prev;
    }, {});

  useEffect(() => {
    dispatch(setFooter(isLoggedIn ? true : false));
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    const profileOnboardingStep = getProfileOnboardingStep();

    if (profileOnboardingStep) return;

    setProfileOnboardingStep(1);
  }, []);

  const renderHeader = () =>
    isLoggedIn ? (
      <LoggedInHeader />
    ) : (
      <Header newStyles={profile?.photos.length === 0 ? styles : {}} />
    );

  const setupInventoryCard = inventories.length === 0 && profileOwner && currentUser.confirmed;

  const addTraitsCard =
    profileOwner &&
    profile.adult &&
    currentUser.confirmed &&
    inventories.length !== 0 &&
    (!profile?.trait_ids || profile?.trait_ids.length === 0);

  return (
    <div
      className={clsx(styles.profileContainer, {
        [styles.paddingBottom]: cartItems?.length > 0 && !isDialog, // when there is a view cart button
        [styles.loggedIn]: isLoggedIn && !isDialog,
        [styles.notLoggedIn]: !isLoggedIn,
      })}
    >
      {!isDialog && renderHeader()}

      {/* Show purple background (modelCover) if user is not logged in and profile is adult or has no photos  */}
      {(!isLoggedIn && profile.adult) || (!isLoggedIn && profile.photos.length === 0) ? (
        <div className={styles.modelCover} />
      ) : (
        <ProfileCover
          setProfile={setProfile}
          profile={profile}
          handleClose={handleClose}
          isDialog={isDialog}
        />
      )}

      <ProfileInfo
        setProfile={setProfile}
        profile={profile}
        uniqInfluencerNetworks={uniqInfluencerNetworks}
        noMarginTopInDialog={isDialog}
        handleInvite={handleInvite}
        isDialog={isDialog}
        gig={gig}
      />

      {profileOwner && <InfluencerActionsSimplified profile={profile} />}

      {((isLoggedIn && profile?.bio) || (!isLoggedIn && !profile.adult)) && (
        <Hidden mdUp>
          <Bio profile={profile} setProfile={setProfile} />
        </Hidden>
      )}

      <Hidden mdUp>
        <WorkingStats working_stats={profile.working_stats} />
      </Hidden>

      {uniqInfluencerNetworks.length > 0 && (
        <Hidden mdUp>
          <InfluencerNetworks profile={profile} influencerNetworks={uniqInfluencerNetworks} />
        </Hidden>
      )}

      {!currentUser.confirmed && isLoggedIn && (
        <div className={styles.centeredDiv}>
          <div className={styles.purpleCard}>
            <PurpleCard
              title="Confirm your account"
              description="Confirm your account to be matched with the world’s largest marketplace of
                  advertisers."
              buttonText="CONFIRM ACCOUNT"
              handleClick={() => router.push('/access-confirmation')}
            />
          </div>
        </div>
      )}

      {setupInventoryCard && (
        <div className={styles.centeredDiv}>
          <PurpleCard
            title="Get full access"
            description="Complete your account setup to get matched with paying brands and gigs."
            buttonText="Setup and verify my socials"
            handleClick={() => router.push('/inventory')}
          />
        </div>
      )}

      {addTraitsCard && (
        <div className={styles.centeredDiv}>
          <PurpleCard
            title="Add your traits"
            description="Update your profile to get matched with paying brands and gigs."
            buttonText="Setup your traits"
            handleClick={() =>
              dispatch(openModal({ name: 'Inventory Edit', data: { step: 'Traits' } }))
            }
          />
        </div>
      )}

      <Grid
        container
        className={clsx(styles.filterContainer, {
          [styles.whiteBackground]: mobile && profileOwner,
          [styles.notLoggedInPadding]: !isLoggedIn,
        })}
      >
        <>
          {inventories.length === 0 && profileOwner && (
            <Alert severity="info" className={styles.alert}>
              This influencer has not finished setting up their inventory yet.
            </Alert>
          )}

          {inventories.length > 0 && (
            <>
              <Hidden smDown>
                <Grid item md={5} lg={4}>
                  <Info profile={profile} setProfile={setProfile} />

                  <WorkingStats working_stats={profile.working_stats} noPaddingLeft={isDialog} />
                </Grid>
              </Hidden>

              <Grid item xs={12} md={7} lg={8}>
                <InventoryTabs
                  uniqInfluencerNetworks={uniqInfluencerNetworks}
                  inventories={inventories}
                  networks={networks}
                  profile={profile}
                  selectedNetworkIds={selectedNetworkIds}
                  isDialog={isDialog}
                />
              </Grid>
            </>
          )}
        </>
      </Grid>

      {!isLoggedIn && inventories.length > 0 && (
        <div className={styles.footerCta}>
          <h3>Add to your cart now</h3>

          <div className={styles.redirectButtons}>
            <Link href="/login" passHref>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setRedirectTo(`/u/${profile.username}`)}
              >
                Login
              </Button>
            </Link>

            <Link
              href={
                profile.adult
                  ? `/sign-up/shoutout?utm_campaign=profile_footer_1_2023&utm_source=${profile.username}&utm_medium=profile_footer`
                  : '/sign-up'
              }
              passHref
            >
              <Button
                color="primary"
                variant="contained"
                onClick={() => setRedirectTo(`/u/${profile.username}`)}
              >
                Sign Up
              </Button>
            </Link>
          </div>

          <ExternalLink
            className={styles.faq}
            mobileUrl="https://gigsocial.crisp.help/en-us/category/advertisers-7fa8h5/"
            desktopUrl="https://gigsocial.crisp.help/en-us/category/advertisers-7fa8h5/"
            target="_blank"
            rel="noopener noreferrer"
            page="Have Questions?"
            preview="url"
          >
            Have questions?
          </ExternalLink>
        </div>
      )}

      <ProfilePreviewDialog profile={profile} setProfile={setProfile} />
    </div>
  );
};

Profile.propTypes = {
  setProfile: PropTypes.func.isRequired,
  profile: PropTypes.object,
  isDialog: PropTypes.bool,
  handleClose: PropTypes.func,
  gig: PropTypes.object,
  handleInvite: PropTypes.func,
};

Profile.defaultProps = {
  profile: {},
  isDialog: false,
  handleClose: () => {},
  gig: {},
  handleInvite: () => {},
};

export default Profile;
